import React from "react";

import { config } from "@/config/environment";

declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
};

type InquiryFormPayload = Record<string, unknown>;

function toStringRecord(payload: InquiryFormPayload): Record<string, string> {
  const out: Record<string, string> = {};

  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (typeof value === "string") {
      out[key] = value;
      continue;
    }

    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
      out[key] = String(value);
      continue;
    }

    if (value instanceof Date) {
      out[key] = value.toISOString();
      continue;
    }

    out[key] = JSON.stringify(value);
  }

  return out;
}

interface SubmitArgs {
  emailEvent: string;
  payload: InquiryFormPayload;
}

export function useInquiryForm() {
  const [error, errorSet] = React.useState(false);
  const [loading, loadingSet] = React.useState(false);
  const [success, successSet] = React.useState(false);

  const submitForm = React.useCallback(async ({ emailEvent, payload }: SubmitArgs) => {
    errorSet(false);
    loadingSet(true);
    successSet(false);

    try {
      const token = await grecaptcha.enterprise.execute(`${config.recaptchaSiteKey}`, {
        action: "submit"
      });

      const formData = toStringRecord(payload);

      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailEvent,
          formData,
          recaptchaToken: token
        })
      });

      if (response.ok) {
        successSet(true);
        errorSet(false);
      } else {
        errorSet(true);
      }
    } catch {
      errorSet(true);
    } finally {
      loadingSet(false);
    }
  }, []);

  return React.useMemo(
    () => ({ error, loading, success, submitForm }),
    [error, loading, success, submitForm]
  );
}
